import { api } from './client';
import type { PAStatus } from './pa';

/**
 * ============================================================================
 * THE ACCESS CLIENT — what a subscriber may do TO HIS OWN ACCOUNTS
 * ============================================================================
 *
 *   GET    /pa/accounts/capabilities              — what THIS installation permits
 *   GET    /pa/accounts/blockings                 — history + pending plans, per Account
 *   POST   /pa/accounts/{id}/block                — «отпуск» (F19)
 *   POST   /pa/accounts/{id}/unblock              — end it (F21)
 *   DELETE /pa/accounts/{id}/schedule/{sid}       — cancel a future-dated plan
 *   POST   /pa/accounts/{id}/activate             — self-activation (F18, D45)
 *   POST   /pa/accounts/{id}/password             — the Account credential (F22)
 *   GET    /pa/accounts/{id}/devices              — the registered Devices (F23)
 *   DELETE /pa/accounts/{id}/devices/{device_id}  — de-register one, freeing a slot
 *
 * It mirrors the wire structs in `backend/internal/personalarea/access_handlers.go`,
 * pinned BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ THREE RULES THIS MODULE AND ITS SCREENS LIVE UNDER
 * ============================================================================
 *
 * 1. **THE CONTRACT TRAVELS IN THE QUERY STRING ON EVERY VERB, INCLUDING THE POSTS AND THE
 *    DELETES.** `Service.moneyScope` is the ONE ownership check on this listener and it
 *    reads `?contract_id=`. A client that put it in a body would be answered `404` by
 *    every write route — the parameter is missing, and a missing one is refused exactly
 *    like a foreign one, so the failure would read as a permissions fault rather than as a
 *    client bug. `wire-spec.test.ts` pins that every one of these functions sends it.
 *
 * 2. **NOTHING HERE RE-DERIVES A SERVER VERDICT.** `can_block` / `can_unblock` are the
 *    server's own answers from the same predicate the write routes apply; the two
 *    `earliest_*_date` values are civil dates in the OPERATOR's billing time zone, which
 *    a browser cannot compute — it does not know `general.billing_timezone` and the
 *    subscriber's laptop may be in any zone at all, so around every local midnight a
 *    browser-derived «today» is simply a different day. A screen that computed either
 *    would be a second statement of a rule that can drift from the one that enforces.
 *
 * 3. **MONEY IS AN INTEGER OF MINOR UNITS AND THIS MODULE CONVERTS NONE OF IT.** The four
 *    figures on `PAActivation` are NULLABLE, and nil is not zero: publishing 0 for
 *    «nobody could tell us what activating costs» drops the bar to the configured minimum
 *    and activates a subscriber into instant suspension. The screen renders «неизвестно».
 */

/** `PACapabilities` — what this installation lets a subscriber do, and from which dates. */
export interface PACapabilities {
  /** `accounts.self_activation_enabled` — the same switch the activate route checks. */
  self_activation_enabled: boolean;
  /** `personal_area.account_password_change_enabled` (Appendix L §2.4, §2.22). */
  account_password_change_enabled: boolean;
  block_same_day_allowed: boolean;
  unblock_same_day_allowed: boolean;
  /**
   * YYYY-MM-DD **in the operator's billing time zone** — today when a same-day start is
   * permitted, tomorrow when it is not. Served rather than derived: see rule 2 above.
   */
  earliest_block_date: string;
  earliest_unblock_date: string;
  /**
   * FALSE on this build, and stated rather than omitted. De-registration archives the
   * binding and frees a slot; the device falls back to the captive state at its NEXT
   * authorization. A confirmation dialog must not promise that the connection drops now.
   */
  device_deregister_cuts_session: boolean;
}

/** `PABlock` — one row of the block HISTORY: what actually happened. */
export interface PABlock {
  id: number;
  /** The Appendix C §2.2 code that was set. */
  status: number;
  set_at: string;
  /** True when the SUBSCRIBER set it — what distinguishes «вы» from «оператор». */
  is_self: boolean;
  removed_at: string | null;
  comment: string;
}

/** `PAScheduledStatus` — one PENDING scheduled change: what is still intended. */
export interface PAScheduledStatus {
  id: number;
  target_status: number;
  /**
   * The instant the change fires, PROJECTED into the operator's billing time zone.
   *
   * ⚠ IT USED TO TRAVEL RAW, and the correction arrived from the server side on 2026-08-12
   * (W4 B3): the write path parses the subscriber's civil date in that zone and the capability
   * read computes its dates in it, but this projection passed the value through untouched — so
   * a plan for the 10th rendered as the 9th for a browser west of a +03 operator. Print
   * [PAScheduledStatus.run_date] instead; this field is kept because a client that needs a real
   * instant has nowhere else to get one.
   */
  run_at: string;
  /**
   * The CIVIL DAY the change fires, YYYY-MM-DD in the operator's zone — **the one field a
   * screen should print.** The subscriber picked a DAY, and no instant can carry a day
   * unambiguously to a client in an unknown zone.
   */
  run_date: string;
  /**
   * ⚠ NON-NULL MEANS THIS ROW IS THE AUTO-ACTIVATION THAT **ENDS** THE BLOCK IT NAMES,
   * not a plan of its own. Cancellation cascades PARENT → child and never child → parent,
   * so cancelling this row would silently convert a timed «отпуск» into an indefinite
   * block. The server refuses it (`pa.cancel_parent_first`) and this field is what lets
   * the screen not offer it at all.
   */
  parent_scheduled_id: number | null;
  comment: string;
  /**
   * ⚠ THE SERVER'S OWN VERDICT ON «Отменить» FOR THIS ROW, and the field this screen must key
   * the button on. It comes from the same predicate `DELETE /pa/accounts/{id}/schedule/{sid}`
   * enforces, so a button offered anywhere else is a button whose only outcome is an error.
   *
   * IT IS NOT `parent_scheduled_id === null`, which is what the screen used to derive it from.
   * That derivation covered the auto-activation CHILD and nothing else, and this list also
   * carries the OPERATOR's plans: a staff-created administrative block was a cancellable row
   * by that rule. `parent_scheduled_id` stays, because it selects the more informative of the
   * two «нельзя» sentences — «это дата окончания» is a different fact from «это план
   * оператора», and the subscriber can act on the second.
   */
  can_cancel: boolean;
}

/** `PAAccountBlockings` — one Account's blocking picture. */
export interface PAAccountBlockings {
  account_id: number;
  login: string;
  /** The Appendix C §2.2 code; the portal owns the Russian labels. */
  status: number;
  /** THE SERVER'S OWN VERDICT — true only for an ACTIVE Account. Never re-derived here. */
  can_block: boolean;
  /** THE SERVER'S OWN VERDICT — true only for a USER block (status 2). */
  can_unblock: boolean;
  history: PABlock[];
  scheduled: PAScheduledStatus[];
}

/** `PABlockings` — every Account of the contract, in ONE read. */
export interface PABlockings {
  contract_id: number;
  items: PAAccountBlockings[];
  total: number;
}

/**
 * `PABlockRequest` — the date a block or unblock takes effect.
 *
 * ⚠ THERE IS NO DURATION AND NO `until`, and the screen must offer neither: that is
 * template-based blocking (F20), which is POST-LAUNCH and has no entity in this system —
 * and an `until` would create the paired auto-activation row whose cancellation is the
 * trap the cancel route refuses. Ending a block early is the unblock route.
 */
export interface PABlockRequest {
  /**
   * YYYY-MM-DD in the operator's billing time zone. Empty or today ⇒ apply NOW (subject
   * to the operator's same-day permission); a future day ⇒ schedule at 00:00 of that day;
   * a PAST day is refused rather than applied, because the engine treats a past effective
   * date as «fire at once» and a mistyped year would otherwise block him today.
   */
  from: string;
}

/** `PABlockAction` — what a block or unblock did. */
export interface PABlockAction {
  account_id: number;
  /**
   * ⚠ THE TWO OUTCOMES ARE ONE FIELD, AND THE SCREEN READS THIS RATHER THAN THE HTTP
   * STATUS. The server answers 200 for «applied now» and 201 for «scheduled»;
   * `apiFetch` returns a decoded body and no status, deliberately, so the outcome a
   * subscriber is told about is the one the server named in the payload.
   */
  applied: boolean;
  status: number;
  /** The pending row when the change was future-dated, null otherwise. */
  scheduled: PAScheduledStatus | null;
}

/** `PAActivation` — the Account's state after a self-activation. */
export interface PAActivation {
  account_id: number;
  activated: boolean;
  status: number;
  /** Re-read AFTER the write — false once he has activated, so the button stops. */
  awaiting_activation: boolean;
  /** MINOR UNITS. `max(minimum activation balance, first charge)`. NULL ≠ 0 — see rule 3. */
  required: number | null;
  /** MINOR UNITS. What must be PAID IN to satisfy the gate. NULL ≠ 0. */
  shortfall: number | null;
  balance: number | null;
  /** MINOR UNITS. Availability minus live reservations — the half the charge is tested on. */
  spendable: number | null;
  /**
   * THE TARIFF-GROUP ROUTING KEY (2026-08-16). True when this Account has NO primary
   * tariff and activates on a plan the SUBSCRIBER picks out of its activation tariff
   * group — the confirmation dialog must offer the plan list before it can offer an
   * «Активировать», and the gate refuses until one is chosen.
   */
  tariff_choice_required: boolean;
  /** The group's name, for the heading above the plans. */
  tariff_group_name?: string;
}

/**
 * `PAActivationOption` — one plan on the choice screen.
 *
 * ⚠ EVERY MONEY FIGURE IS IN MINOR UNITS, like the rest of this surface. The
 * walled-garden portal's twin of this type uses DECIMAL STRINGS, and a field copied
 * between the two without conversion is wrong by a factor of a hundred.
 *
 * The two prices answer different questions and both must be shown: `subscription_fee`
 * is what the plan costs per period, `activation_charge` is what pressing the button
 * debits right now (the prorated remainder of the current one).
 */
export interface PAActivationOption {
  tariff_id: number;
  name: string;
  description: string;
  /** 'A' | 'B' | 'C' — stated in words on the screen, never as the letter. */
  billing_model: string;
  bandwidth_kbps?: number;
  subscription_fee: number;
  activation_charge: number;
  /** False = nobody could price this plan; the figure is a placeholder and the verdict false. */
  charge_known: boolean;
  required: number;
  shortfall: number;
  can_activate: boolean;
}

/** `PAActivationOptions` — the whole plan-choice surface for one Account. */
export interface PAActivationOptions {
  account_id: number;
  /**
   * TRUE with an empty `options` is a real state and a MISCONFIGURATION: the screen must
   * say so rather than present an empty list as though it were normal.
   */
  choice_required: boolean;
  group_name?: string;
  group_archived: boolean;
  min_balance: number;
  balance: number;
  availability: number;
  funds_known: boolean;
  options: PAActivationOption[];
}

/** `PAAccountPasswordRequest` — F22. C18 binds the NEW value only. */
export interface PAAccountPasswordRequest {
  current_password: string;
  new_password: string;
}

/** `PADevice` — one registered Device as its owner may see it (§2.20). View-only. */
export interface PADevice {
  id: number;
  /**
   * ⚠ «mac_option82» | «mac_trap_port» | «mac_only» — the values migration 0038's CHECK
   * constraint admits, and the ONLY three this server can send. It is `string` rather than a
   * union because the set is open by design (Amendment 02 §4.1), not because it is unknown.
   *
   * ~~«mac» | «mac_opt82» | «mac_trap»~~ — STRUCK 2026-08-12. That trio was copied from the
   * Go doc comment and this schema's own prose, both of which listed forms no column has ever
   * held, and the label table copied from here printed the raw slug to the subscriber. The
   * fixture repeated it, so the screen's own test was green on the wrong answer.
   *
   * ⚠ THE VOCABULARY IS NOW DECLARED AS AN `enum` ON THE SCHEMA, and the portal's Russian
   * labels are pinned to it in both directions by `wire-spec.test.ts` — so this comment is no
   * longer the thing standing between a new form and an ASCII slug on a subscriber's device
   * card. It stays `string` because the set is open: the guard's job is that a value the SERVER
   * declares has a label, not that the client refuses one it has never heard of.
   */
  identity_form: string;
  mac: string;
  opt82_circuit: string | null;
  opt82_remote: string | null;
  /** The learned switch PORT. The switch's own id is deliberately not on the wire. */
  trap_port: number | null;
  /** "self" (registered on the enrollment portal) or "manager". */
  origin: string;
  registered_at: string;
  last_online_at: string | null;
  /** Joined ON THE SERVER from the Account's own leases. "" when it holds none — or when
   *  the join could not be made, which `address_known` is what distinguishes. */
  address: string;
  lease_state: string;
  /**
   * ⚠ FALSE MEANS «WE COULD NOT LOOK», not «he has no lease», and the screen must render
   * «неизвестно» rather than «нет адреса». The installation has no IP-pool service or the
   * read failed; an empty `address` then says nothing at all about the device, and a
   * subscriber diagnosing his own connection reads the two facts differently.
   */
  address_known: boolean;
}

/** `PADevices` — the Account's devices, its limit and the slots in use. */
export interface PADevices {
  account_id: number;
  /** `account.device_limit` (staff-set, default 1). At limit 1 a new registration on the
   *  enrollment portal AUTO-REPLACES the existing device, which is why §2.20 requires it. */
  device_limit: number;
  /** The slots in use, per the SERVER. Never `items.length`: they can differ. */
  used: number;
  items: PADevice[];
  total: number;
}

// ---- calls ------------------------------------------------------------------

/**
 * getCapabilities is authenticated but NOT contract-scoped — every value is
 * installation-wide, so there is no id to own and no `contract_id` to send.
 */
export function getCapabilities(): Promise<PACapabilities> {
  return api.get<PACapabilities>('/pa/accounts/capabilities');
}

/**
 * listBlockings answers for EVERY Account of the contract in ONE request, which is what
 * the §13A-1 picker needs — so the screen never fans out per row.
 */
export function listBlockings(contractID: number): Promise<PABlockings> {
  return api.get<PABlockings>(`/pa/accounts/blockings?contract_id=${contractID}`);
}

/**
 * blockAccount puts one Account on hold.
 *
 * `from` is sent as given, including empty — the server reads empty as «now», and a client
 * that substituted a browser-derived date for it would be deciding the operator's civil
 * day for him (rule 2).
 */
export function blockAccount(
  contractID: number,
  accountID: number,
  from: string,
): Promise<PABlockAction> {
  const body: PABlockRequest = { from };
  return api.post<PABlockAction>(
    `/pa/accounts/${accountID}/block?contract_id=${contractID}`,
    body,
  );
}

export function unblockAccount(
  contractID: number,
  accountID: number,
  from: string,
): Promise<PABlockAction> {
  const body: PABlockRequest = { from };
  return api.post<PABlockAction>(
    `/pa/accounts/${accountID}/unblock?contract_id=${contractID}`,
    body,
  );
}

/**
 * cancelScheduledStatus cancels a future-dated plan.
 *
 * ⚠ The screen must not offer this for a row whose `parent_scheduled_id` is non-null — see
 * `PAScheduledStatus`. The server refuses it, so a client that offered it anyway would put
 * a button on screen whose only outcome is an error the subscriber cannot act on.
 */
export function cancelScheduledStatus(
  contractID: number,
  accountID: number,
  scheduleID: number,
): Promise<PAStatus> {
  return api.del<PAStatus>(
    `/pa/accounts/${accountID}/schedule/${scheduleID}?contract_id=${contractID}`,
  );
}

/**
 * activateAccount turns on a brand-new Account.
 *
 * The gate, the charge, the reservation and the NAS consequence are all the engine's — this
 * is a second CALLER of the path the enrollment portal has driven end to end on the
 * physical NAS since Phase 3, not a second implementation. So the quote and the charge
 * cannot disagree here: neither is computed on this surface.
 */
export function activateAccount(
  contractID: number,
  accountID: number,
  tariffID?: number,
): Promise<PAActivation> {
  return api.post<PAActivation>(
    `/pa/accounts/${accountID}/activate?contract_id=${contractID}`,
    // The chosen plan, when the Account activates out of a tariff group. Omitted
    // entirely otherwise, which is byte-for-byte the pre-2026-08-16 request.
    tariffID == null ? undefined : { tariff_id: tariffID },
  );
}

/**
 * getActivationOptions lists the plans this Account may activate on, each priced FOR IT.
 *
 * A separate call from the dashboard because each option costs the core one activation
 * quote; the cheap routing key (`tariff_choice_required`) already rides on `PAActivation`
 * and on the account row, so the screen knows whether to make this call at all.
 *
 * It holds no policy: which plans are offered, what each costs and whether each is
 * affordable are the accounts domain's answers, from the one gate arithmetic the
 * walled-garden portal and the staff card also read.
 */
export function getActivationOptions(
  contractID: number,
  accountID: number,
): Promise<PAActivationOptions> {
  return api.get<PAActivationOptions>(
    `/pa/accounts/${accountID}/activation-options?contract_id=${contractID}`,
  );
}

/**
 * changeAccountPassword sends the current and the new value — and NOT the confirmation
 * field the screen shows, for the reason the portal-password form states: a server that
 * receives two copies of a new password can only compare them, which the browser already
 * did.
 *
 * ⚠ C18 IS NOT RE-IMPLEMENTED IN THIS APP. The character set is enforced by
 * `internal/users` and reported as `400 pa.password_charset` with a Russian message the
 * screen displays verbatim; a second copy here is a copy that drifts, and the drifted one
 * is the one the subscriber believes.
 */
export function changeAccountPassword(
  contractID: number,
  accountID: number,
  currentPassword: string,
  newPassword: string,
): Promise<PAStatus> {
  const body: PAAccountPasswordRequest = {
    current_password: currentPassword,
    new_password: newPassword,
  };
  return api.post<PAStatus>(
    `/pa/accounts/${accountID}/password?contract_id=${contractID}`,
    body,
  );
}

export function listDevices(contractID: number, accountID: number): Promise<PADevices> {
  return api.get<PADevices>(`/pa/accounts/${accountID}/devices?contract_id=${contractID}`);
}

/**
 * deregisterDevice archives the BINDING and frees a slot.
 *
 * ⚠ IT DOES NOT CUT A LIVE SESSION. There is no `device.deregistered` event anywhere in
 * this system: the domain soft-deletes and returns, with no outbox event, no lease release
 * and no CoA, and the device falls back to the captive state at its NEXT authorization.
 * `device_deregister_cuts_session` on the capability read says so, and the confirmation
 * dialog reads it rather than a wave's memory.
 *
 * Idempotent: a second call on the same Account's already-archived device answers 200.
 */
export function deregisterDevice(
  contractID: number,
  accountID: number,
  deviceID: number,
): Promise<PAStatus> {
  return api.del<PAStatus>(
    `/pa/accounts/${accountID}/devices/${deviceID}?contract_id=${contractID}`,
  );
}
